import type { z } from "zod";

/**
 * Validate an entity payload before it is written to Firestore.
 *
 * The client write layer is the last line of defense before persistence: the
 * Firestore security rules grant blanket field writes (no schema enforcement),
 * so if this layer does not validate, invalid documents reach the database. The
 * bulk importer in particular can feed leftover temporary fields (e.g.
 * `modifierGroupNames`) and the occasional unmapped column.
 *
 * This helper:
 *  1. Strips keys the strict schema does not recognize, so benign extras (such
 *     as importer temp fields) do not trip strict validation, and so unknown
 *     columns are never persisted.
 *  2. Strictly validates the sanitized payload.
 *  3. Throws a descriptive error on failure — invalid data is never silently
 *     written (replaces the old warn-and-write behavior).
 *
 * Returns the sanitized payload (known keys only). The caller is responsible for
 * appending server timestamps / removing undefined values before writing.
 */
export function validateForWrite(
  schema: z.ZodTypeAny,
  allowedKeys: Iterable<string>,
  data: Record<string, unknown>,
  label: string,
): Record<string, unknown> {
  const allowed =
    allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys);

  const sanitized: Record<string, unknown> = {};
  for (const key of Object.keys(data)) {
    if (allowed.has(key)) {
      sanitized[key] = data[key];
    }
  }

  const result = schema.safeParse(sanitized);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("; ");
    throw new Error(`${label} failed schema validation: ${issues}`);
  }

  return sanitized;
}
